import {
    ComponentObject,
    ComponentSelectors,
    ComponentElements,
    ComponentService,
    ComponentInjector,
    ComponentSelector,
    UIComponent,
    Inject,
    Injectable,
} from '@atypon/ui-utils';
import {keyCodes} from '../../../utility/js/utils'

import { VPortListener } from "ui-core/plugins/enquireJs/js/helper"

interface FootnoteComponents {
    noteContainer: HTMLElement | null;
    chevronDown: HTMLElement | null;
    noteClose: HTMLElement | null;
}

@Injectable()
class Configs {
    notePopup(content: string): string {
        return `<button class="footNoteClose footNote__cycleElement" aria-label="close button">
                    <i class="icon-tools_close" aria-hidden="true"></i>
                </button>
                <div class="footNotePopup__item footNote__cycleElement">${content}</div>
                <span class="chevronDown"></span>` };

}

class Texts extends ComponentObject { }

class ClassList extends ComponentObject {
    accordionControl = '.accordion__control';
    jsOpen = 'js--open';
    citedByArticleSection = '.article-section__citedBy';
}

class Selectors extends ComponentSelectors {
    supportInfo = new ComponentSelector("a[href$='#support-information-section']", "document");
    supportInfoControl = new ComponentSelector('.article-section__supporting a.accordion__control', "document");
    openResearch = new ComponentSelector("a[href$='#open-research-section']", "document");
    openResearchControl = new ComponentSelector('.article-section__open-research a.accordion__control', "document");
    links = new ComponentSelector('[data-db-target-of=sections] a, .scrollableLink, .scrollableLinks a, .comment-link a, .PdfLink a[href="#accessDenialLayout"], .article-section__full a[href*="#"], [href="#citedby-section"]', 'document');

}

class Elements extends ComponentElements<Selectors> {
    supportInfo: HTMLAnchorElement;
    supportInfoControl: HTMLAnchorElement;
    openResearch: HTMLAnchorElement;
    openResearchControl: HTMLAnchorElement;
    links: HTMLElement[] = []
    noteLink: HTMLAnchorElement;
}


interface GoToSection extends ComponentService<Selectors, Elements, ClassList, Texts> { }


@ComponentInjector(Selectors, Elements, ClassList, Texts)
class GoToSection implements UIComponent {


    @Inject()
    protected configs: Configs;


    @VPortListener('md')
    isTablet = false;


    constructor(readonly wrapper: HTMLElement) { }

    initialize(): void {
        this.elements.initialize(this.wrapper);
        this.addEventListeners();
    }

    isTabletOn() {
        this.isTablet = true;
    }

    isTabletOff() {
        this.isTablet = false;
    }

    addEventListeners(): void {
        this.domUtils.addEventListener(this.elements.supportInfo, 'click', () => this.elements.supportInfoControl.click())

        this.domUtils.addEventListener(this.elements.openResearch, "click", () => this.elements.openResearchControl.click())

        this.domUtils.addEventListener(this.elements.links, "click", (e) => this.flowControl(e))

        this.domUtils.addEventListener(this.elements.links, "keydown", (e) => this.flowControl(e))

        this.domUtils.addEventListener(document, "keydown", (e: KeyboardEvent) => this.escapeKeyHandler(e))
    }

    flowControl(e):void{

        if(e.type === "keydown" && e.keyCode !== keyCodes.RETURN) return;

        const element = e.currentTarget as HTMLAnchorElement

        if (
            element.classList.contains('open-figure-link') ||
            element.classList.contains('bibLink') ||
            element.classList.contains('tab-link') ||
            element.closest('.accordion__control') ||
            element.closest('.figure__toggle-link')
        ) {
            return;
        } else {

            e.preventDefault();
            e.stopPropagation();
            this.domUtils.getElements("html , body").forEach(e => e.classList.remove('lock-screen'))

            if (element.classList.contains('noteLink')) {
                this.openFootNote(element);
                window.addEventListener('resize', () => {
                    if (this.domUtils.getElements('.footNotePopup').length) {
                        this.openFootNote(element);
                    }
                });
            } else if (element.classList.contains('backToText')) {
                this.domUtils.getElement(element.getAttribute('href')).focus();
            } else if (
                element.classList.contains('goToText') ||
                element.closest('.article-header__references-container') ||
                element.getAttribute('href').indexOf('#accessDenialLayout') > -1
            ) {
                this.scrollToTarget(element, true);
                this.domUtils.getElement(element.getAttribute('href')).focus();
            } else {
                this.scrollToTarget(element, true);
                (window as any).UX.dropBlock.on.hide();
                if (this.isTablet) {
                    this.domUtils.getElement('[data-db-target-of=sections]').click();
                    this.domUtils.getElement('.w-slide__back').click();
                }
            }

            if (element.closest('.sections__drop.js--open')) {
                (window as any).UX.dropBlock.on.hide();
            }
        }
    }

    escapeKeyHandler(event: KeyboardEvent):void{
        if (event.key === "Escape") {
            this.closeFootNote();
        }
    }

    scrollToTarget(element: HTMLAnchorElement, refID: boolean): void {
        let target = refID ? this.domUtils.getElementById(element.hash.slice(1)) : element;
        target = target ? target : this.domUtils.getElement('[name="' + element.hash.slice(1) + '"]')
        const clickTarget = target?.closest(this.classList.accordionControl) as HTMLElement;
        if (clickTarget && target?.closest(this.classList.citedByArticleSection)) {
            const citedBySection = target?.closest(this.classList.citedByArticleSection) as HTMLElement,
                accordionControl = this.domUtils.getElement(this.classList.accordionControl, citedBySection)

            if (!accordionControl.classList.contains(this.classList.jsOpen)) {
                accordionControl.click();
            }
        } else if (
            clickTarget &&
            !clickTarget.classList.contains(this.classList.jsOpen)
        ) {
            clickTarget.click();
        }

        if (target) {
            
            target.scrollIntoView({ behavior: "smooth", block: "start" });
            target.setAttribute('tabindex', '0')
            setTimeout(() => {
                target.focus();
            }, 750)

            if (target.tagName !== 'a' && target.tagName !== 'button') {
                target.setAttribute('tabindex', '-1');
            }
        }
        else {
            this.redirectToExternalLink(element);
        }
    }

    redirectToExternalLink(element: HTMLAnchorElement): void {
        window.location.assign(element.href);
    }

    openFootNote(noteLink: HTMLElement): void {
        this.closeFootNote(); // close the previous footnote if exist
        this.elements.noteLink = noteLink as HTMLAnchorElement;

        const { noteContainer, chevronDown, noteClose } = this.createFootNote(noteLink),
            parentTable = this.isNodeInsideNode(noteLink, 'TBODY'); // return the parent table if exist , otherwise return null
        if (parentTable instanceof HTMLElement) {
            parentTable.appendChild(noteContainer);
        } else {
            document.body.appendChild(noteContainer);
        }
         (window as any).UX.utils.focusCycleInitiator(noteContainer);
        const footNotePopup = noteContainer.querySelector('.footNotePopup__item') as HTMLElement;
        footNotePopup.focus({ preventScroll: true });  

        let noteHeight = noteContainer.offsetHeight,
            topOffset = 0,
            leftOffset = 0,
            leftOffsetToTheParent = 0,
            parentWidth = 0,
            parentHeight = 0;

        if (parentTable instanceof HTMLElement) {
            topOffset = noteLink.offsetTop;
            leftOffset = noteLink.offsetLeft;
            leftOffsetToTheParent = noteLink.offsetLeft - parentTable.offsetLeft;
            parentWidth = parentTable.offsetWidth;
            parentHeight = parentTable.offsetHeight;
            noteContainer.classList.add('insideTable');
        } else {
            topOffset = noteLink.getBoundingClientRect().top + document.documentElement.scrollTop;
            leftOffset = noteLink.getBoundingClientRect().left;
            leftOffsetToTheParent = noteLink.offsetLeft;
            parentWidth = noteLink.parentElement.offsetWidth;
        }

        if (this.isNodeInsideNode(noteLink, 'THEAD')) {
            chevronDown.classList.add('inverse');
            noteContainer.style.top = `${topOffset + 35}px`;
            noteContainer.classList.add('insideHead');
        } else {
            noteContainer.style.top = `${topOffset - noteHeight - 5}px`;
        }

        // to change the left & right direction based on the noteLink position
        if (parentWidth / 2 > leftOffsetToTheParent) {
            noteContainer.style.left = `${leftOffset + 2}px`;
            chevronDown.classList.add('left');
        } else {
            noteContainer.style.left = `${leftOffset - noteContainer.offsetWidth + 22}px`;
            chevronDown.classList.add('right');
        }

        // to change the up & down direction based on the noteLink position
        if (parentHeight / 2 > topOffset) {
            chevronDown.classList.add('inverse');
            noteContainer.style.top = `${topOffset + 35}px`;
        }

        noteClose.addEventListener('click', () => this.closeFootNote());
    }

    closeFootNote(): void {
        if(this.elements.noteLink || this.domUtils.getElement(".footNotePopup")){
            this.elements.noteLink?.setAttribute('aria-expanded', 'false')
            this.elements.noteLink?.removeAttribute('aria-controls')
            this.domUtils.getElement(".footNotePopup")?.remove();
            this.elements.noteLink?.focus();
            this.elements.noteLink = null;
        }
    };

    createFootNote(noteLink: HTMLElement): FootnoteComponents {
        const noteContainer: HTMLElement = document.createElement('div');
        noteContainer.classList.add("footNotePopup");

        const noteID = noteLink.getAttribute('data-noteid'),
            controllerId = noteLink.getAttribute('id'),
            popupId = noteLink.getAttribute('href').substring(1), // to remove '#'
            noteIDList= noteID?.split(' ');

        if (noteIDList.length > 1) {

            const notesContentContainer: HTMLElement = document.createElement('div');

            noteIDList.forEach(noteElement => {
                const noteContent = document.querySelector(`li#${noteElement}`).cloneNode(true);

                notesContentContainer?.appendChild(noteContent);
            });

            noteContainer.innerHTML = this.configs.notePopup(notesContentContainer.innerHTML);
        }
        else {
            const noteContent = document.querySelector(`li#${noteID}`).innerHTML;
            noteContainer.innerHTML = this.configs.notePopup(noteContent);
        }

        
        noteLink.setAttribute('aria-expanded', 'true');
        noteLink.setAttribute('aria-controls', popupId);
        noteContainer.setAttribute('id', popupId);
        noteContainer.setAttribute('aria-labelledby', controllerId);

        const chevronDown = this.domUtils.getElement('.chevronDown', noteContainer),
            noteClose = this.domUtils.getElement('.footNoteClose', noteContainer);

        return { noteContainer, chevronDown, noteClose };
    }

    // to determine if the notelink exist inside XYZ node or not because different styles going to be applied if yes.
    // return false if not , if true return the XYZ node itself .
    isNodeInsideNode(node: HTMLElement, parentNode: string): boolean | HTMLElement {
        if (node.tagName === parentNode) return node;
        return node.parentNode !== document.body && this.isNodeInsideNode(node.parentNode as HTMLElement, parentNode);
    }

}

export default GoToSection;